import { z } from 'zod'
import { UuidSchema } from '../../../utils/zodUuid'
import { ConfiguredFieldValuesInputSchema } from './configuredFieldSchemas'
import {
  createLongTextValueSchema,
  createSelectionValueSchema,
} from './fieldValueSchemas'
import type {
  EditAvailability,
  EditField,
  EditOptions,
  EditValues,
} from './editing'

const unavailableForUserReason =
  'No fields are editable in this status with your permissions.'

/** Normalizes record and field identity before field-level authorization. */
export const EditRecordRequestSchema = z
  .object({
    id: UuidSchema,
    values: ConfiguredFieldValuesInputSchema,
  })
  .strict()

/**
 * Compares edit values after trimming text, normalizing empty values, and
 * ignoring selection order and ID casing.
 */
export function equalEditValues(left: unknown, right: unknown): boolean {
  const normalize = (value: unknown) => {
    if (typeof value === 'string') return value.trim() || null
    if (Array.isArray(value))
      return value
        .map((item) => (typeof item === 'string' ? item.toLowerCase() : item))
        .sort()
    return value ?? null
  }
  return JSON.stringify(normalize(left)) === JSON.stringify(normalize(right))
}

/** Builds the value schema for one resolved edit field. */
export function editFieldSchema(field: EditField) {
  if (field.type === 'long_text' || field.type === 'comments')
    return createLongTextValueSchema(!!field.required)

  return createSelectionValueSchema(!!field.required, field.single).superRefine(
    (value, context) => {
      const allowed = new Set(field.options.map(({ id }) => id))
      if (value.some((id) => !allowed.has(id)))
        context.addIssue({
          code: 'custom',
          message: 'Select available options for this field',
        })
      if (
        field.required === 'all' &&
        field.options.some(({ id, active }) => active && !value.includes(id))
      )
        context.addIssue({
          code: 'custom',
          message: 'Every active option is required',
        })
    }
  )
}

/**
 * Validates a sparse edit patch against the supplied resolved fields. Omitted
 * fields remain unchanged but must still satisfy their rules through their
 * saved values, so a required field cannot stay empty by being left out of the patch.
 * Read-only inputs must match their transformed initial values.
 */
export function createEditValuesSchema(fields: readonly EditField[]) {
  return ConfiguredFieldValuesInputSchema.transform((values, context) => {
    const inputs = fields.filter((field) => field.input)
    const allowed = new Set(inputs.map(({ id }) => id))
    for (const id of Object.keys(values)) {
      if (!allowed.has(id))
        context.addIssue({
          code: 'custom',
          path: [id],
          message: 'This field cannot be edited',
        })
    }
    const normalized: EditValues = {}
    for (const field of inputs) {
      if (!Object.prototype.hasOwnProperty.call(values, field.id)) {
        // Comment history is never resubmitted, so an omitted comment is an
        // empty draft (its saved baseline is null).
        const unchanged = editFieldSchema(field).safeParse(field.savedValue)
        if (!unchanged.success)
          for (const issue of unchanged.error.issues)
            context.addIssue({ ...issue, path: [field.id, ...issue.path] })
        continue
      }
      const parsed = editFieldSchema(field).safeParse(values[field.id])
      if (!parsed.success) {
        for (const issue of parsed.error.issues)
          context.addIssue({ ...issue, path: [field.id, ...issue.path] })
      } else {
        normalized[field.id] = parsed.data
        if (field.readOnly && !equalEditValues(parsed.data, field.initialValue))
          context.addIssue({
            code: 'custom',
            path: [field.id],
            message: 'Value must match the configured read-only value',
          })
      }
    }
    return normalized
  })
}

/** Returns the transformed starting values for fields that accept input. */
export function initialEditValues(fields: readonly EditField[]): EditValues {
  return Object.fromEntries(
    fields
      .filter(({ input }) => input)
      .map((field) => [field.id, field.initialValue])
  )
}

/** Orders required fields first, then presents the remaining fields by name. */
export function orderEditFields(fields: readonly EditField[]): EditField[] {
  return [...fields].sort(
    (left, right) =>
      Number(!!right.required) - Number(!!left.required) ||
      left.name.localeCompare(right.name) ||
      left.id.localeCompare(right.id)
  )
}

/** Filters project pay items to the options a pay-item edit field accepts. */
export function payItemEditOptions(
  payItems: readonly EditField['options'][number][],
  forbidDeleted: boolean
): EditField['options'] {
  return payItems.filter(({ active }) => !forbidDeleted || active)
}

/**
 * Supplies project pay items as pay-item field options. Responses omit them
 * because the project-wide list would otherwise repeat for every record.
 */
export function withPayItemEditOptions(
  options: EditOptions,
  payItems: readonly EditField['options'][number][]
): EditOptions {
  return {
    ...options,
    fields: options.fields.map((field) =>
      field.type === 'pay_items'
        ? {
            ...field,
            options: payItemEditOptions(payItems, !!field.forbidDeleted),
          }
        : field
    ),
  }
}

/**
 * Whether a field can accept a change: an editable input, or a read-only input
 * whose configured initial value still differs from its saved value.
 */
export function acceptsEditChange({
  input,
  readOnly,
  savedValue,
  initialValue,
}: EditField): boolean {
  return input && (!readOnly || !equalEditValues(savedValue, initialValue))
}

/**
 * Projects actor-neutral options for one user. Intrinsic context fields remain
 * visible, while input fields require a direct grant or elevated project access.
 */
export function resolveEditOptionsForUser(
  options: EditOptions,
  userId: string,
  elevated: boolean
): EditOptions {
  const fields = options.fields.filter(
    (field) =>
      !field.input || elevated || field.editableByUserIds.includes(userId)
  )
  if (!options.enabled) return { ...options, fields }
  const enabled = fields.some(acceptsEditChange)
  return {
    ...options,
    fields,
    enabled,
    reason: enabled ? null : unavailableForUserReason,
  }
}

/** Summarizes resolved options into the availability a records list needs. */
export function editAvailability(
  options: Pick<EditOptions, 'enabled' | 'reason' | 'fields'>
): EditAvailability {
  return {
    enabled: options.enabled,
    reason: options.reason,
    editorIds: [
      ...new Set(
        options.fields
          .filter(acceptsEditChange)
          .flatMap(({ editableByUserIds }) => editableByUserIds)
      ),
    ].sort(),
  }
}

/**
 * Projects availability for one user with the same outcome as
 * {@link resolveEditOptionsForUser}. Enabled options always contain a field
 * that accepts a change, so elevated users need no direct grant.
 */
export function resolveEditAvailabilityForUser(
  availability: EditAvailability,
  userId: string,
  elevated: boolean
): Pick<EditAvailability, 'enabled' | 'reason'> {
  if (!availability.enabled)
    return { enabled: false, reason: availability.reason }
  const enabled = elevated || availability.editorIds.includes(userId)
  return { enabled, reason: enabled ? null : unavailableForUserReason }
}

/**
 * Produces a sparse patch containing user edits and transformed inputs whose
 * final values differ from their saved database baselines.
 */
export function changedEditValues(
  fields: readonly EditField[],
  values: EditValues
): EditValues {
  return Object.fromEntries(
    fields
      .filter(
        (field) =>
          field.input &&
          Object.prototype.hasOwnProperty.call(values, field.id) &&
          !equalEditValues(values[field.id], field.savedValue)
      )
      .map((field) => [field.id, values[field.id]])
  )
}
